import { useRef, useState } from 'react';
import type { TrackerSet } from '@/domain/content.types';
import { isBackupV1 } from '@/domain/progress.types';
import { useT } from '@/i18n/useT';
import { useProgress } from '@/state/progressStore';
import { useSettings } from '@/state/settingsStore';
import { SyncSection } from './SyncSection';

interface SettingsSheetProps {
  set: TrackerSet;
  onClose: () => void;
}

export function SettingsSheet({ set, onClose }: SettingsSheetProps) {
  const { t, lang } = useT();
  const [message, setMessage] = useState<{ text: string; bad?: boolean } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const showBothNames = useSettings((s) => s.showBothNames);
  const setShowBothNames = useSettings((s) => s.setShowBothNames);
  const scanlines = useSettings((s) => s.scanlines);
  const setScanlines = useSettings((s) => s.setScanlines);

  const exportBackup = useProgress((s) => s.exportBackup);
  const importBackup = useProgress((s) => s.importBackup);
  const clearSet = useProgress((s) => s.clearSet);
  const pruneOrphans = useProgress((s) => s.pruneOrphans);

  const onExport = async (): Promise<void> => {
    const backup = await exportBackup();
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `meowtracker-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onImport = async (file: File, mode: 'replace' | 'merge'): Promise<void> => {
    try {
      const parsed: unknown = JSON.parse(await file.text());
      if (!isBackupV1(parsed)) throw new Error('bad format');
      await importBackup(parsed, mode);
      setMessage({ text: t('settings.importDone') });
    } catch {
      setMessage({ text: t('settings.importFailed'), bad: true });
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-black/60"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('settings.title')}
        className="flex h-full w-full max-w-sm flex-col overflow-y-auto border-l border-line bg-surface"
      >
        <header className="sticky top-0 flex items-center justify-between border-b border-line bg-surface px-3 py-2">
          <h2 className="text-[13px] leading-5 font-semibold text-pip">{t('settings.title')}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="rounded-xs border border-line px-1.5 py-0.5 text-[12px] leading-4 text-text-faint hover:text-pip"
          >
            ✕
          </button>
        </header>

        <div className="flex flex-col gap-4 px-3 py-3 text-[12px] leading-4 text-text-dim">
          <section className="flex flex-col gap-2">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={showBothNames}
                onChange={(e) => setShowBothNames(e.target.checked)}
                className="accent-pip"
              />
              {t('settings.showBothNames')}
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={scanlines}
                onChange={(e) => setScanlines(e.target.checked)}
                className="accent-pip"
              />
              {t('settings.scanlines')}
            </label>
          </section>

          <SyncSection />

          <section className="flex flex-col gap-2 border-t border-line pt-3">
            <h3 className="text-[11px] leading-4 font-semibold tracking-wide text-text-faint uppercase">
              {t('settings.data')}
            </h3>
            <p className="text-[11px] leading-4 text-text-faint">{t('settings.storageNote')}</p>

            <button
              type="button"
              onClick={() => void onExport()}
              className="rounded-xs border border-line bg-surface-2 px-2 py-1.5 text-left hover:border-pip-dim hover:text-pip"
            >
              {t('settings.export')}
            </button>

            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                const mode = e.target.dataset['mode'] === 'merge' ? 'merge' : 'replace';
                if (file) void onImport(file, mode);
                e.target.value = '';
              }}
            />
            <div className="grid grid-cols-2 gap-2">
              {(['merge', 'replace'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => {
                    if (fileRef.current) {
                      fileRef.current.dataset['mode'] = mode;
                      fileRef.current.click();
                    }
                  }}
                  className="rounded-xs border border-line bg-surface-2 px-2 py-1.5 hover:border-pip-dim hover:text-pip"
                >
                  {mode === 'merge' ? t('settings.importMerge') : t('settings.importReplace')}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => void pruneOrphans()}
              className="rounded-xs border border-line bg-surface-2 px-2 py-1.5 text-left hover:border-pip-dim hover:text-pip"
            >
              {t('settings.prune')}
            </button>

            <button
              type="button"
              onClick={() => {
                if (window.confirm(t('settings.resetConfirm', { name: set.title[lang] }))) {
                  void clearSet(set.id);
                }
              }}
              className="rounded-xs border border-danger/40 px-2 py-1.5 text-left text-danger hover:bg-danger/10"
            >
              {t('settings.resetSet')}
            </button>

            {message ? (
              <p className={message.bad ? 'text-danger' : 'text-pip'} role="status">
                {message.text}
              </p>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
}
